import { useTranslation } from 'react-i18next';
import { EyeOff } from 'lucide-react';
import type { ReviewImage } from '@/lib/campaigns/reviewImages';

type ReviewImagesStripProps = {
  images: ReviewImage[];
  /** When true, private images get a badge/muted style (admin views). */
  showVisibility?: boolean;
  className?: string;
};

/** Presentational thumbnail strip; each thumbnail opens the full image in a new tab. */
export function ReviewImagesStrip({
  images,
  showVisibility = false,
  className = '',
}: ReviewImagesStripProps) {
  const { t } = useTranslation();
  if (images.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="review-images-strip">
      {images.map((image, index) => {
        const isPrivate = showVisibility && image.visibility === 'private';
        return (
          <a
            key={`${image.url}-${index}`}
            href={image.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`relative block h-16 w-16 overflow-hidden rounded-md border ${
              isPrivate ? 'border-dashed border-muted-foreground/60' : 'border-border'
            }`}
          >
            <img
              src={image.url}
              alt=""
              className={`h-full w-full object-cover ${isPrivate ? 'opacity-70' : ''}`}
            />
            {isPrivate ? (
              <span className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-0.5 bg-background/80 text-[10px] text-muted-foreground">
                <EyeOff className="h-2.5 w-2.5" />
                {t('admin.campaigns.submissions.reviewImages.private')}
              </span>
            ) : null}
          </a>
        );
      })}
    </div>
  );
}
